import { defineStore } from 'pinia';

import { GatewayApiClient } from '@evidentid/gateway-api-client';
import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
import { RbacRoleDetails } from '@evidentid/tprm-portal-lib/models/rbac/RbacRoleDetails.model';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

import { useUserStore } from '../auth/store/user';
import { usePersistingErrorStore } from '../persisting-error/store';

import { hasSufficientFeatures } from './utils/has-sufficient-features/hasSuffiencientFeatures';

export interface RbacRequirements {
    gatewayClient: GatewayApiClient;
    router: { currentRoute: { value: { params: Record<string, any> } } };
}

export interface UserRolesStatus {
    status: OperationStatus;
    list: RbacRoleDetails[];
}

export interface RbacState {
    userRoles: UserRolesStatus;
}

function appOf(store: { $app: any }): RbacRequirements {
    return store.$app;
}

const waitForUserRoles = (store: { userRoles: UserRolesStatus }) => new Promise((resolve, reject) => {
    let timeoutId: any = null;

    const checkStatus = (stateObj: { status: OperationStatus }) => {
        if (stateObj.status === OperationStatus.success) {
            resolve(true);
            return true;
        } else if (stateObj.status === OperationStatus.error) {
            reject();
        }
        return false;
    };
    const intervalId = setInterval(() => {
        if (checkStatus(store.userRoles)) {
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
            }
            clearInterval(intervalId);
        }
    }, 100);

    timeoutId = setTimeout(() => {
        clearInterval(intervalId);
    }, 5000);
});

export const useRbacStore = defineStore('rbac', {
    state: (): RbacState => ({
        userRoles: {} as UserRolesStatus,
    }),
    actions: {
        startLoadingUserRoles() {
            this.userRoles = {
                status: OperationStatus.loading,
                list: [],
            };
        },
        failLoadingUserRoles() {
            this.userRoles = {
                status: OperationStatus.error,
                list: [],
            };
        },
        finishLoadingUserRoles(payload: RbacRoleDetails[]) {
            this.userRoles = {
                status: OperationStatus.success,
                list: payload,
            };
        },
        async loadUserRoles(rpName: string) {
            const status = this.userRoles.status || OperationStatus.uninitialized;
            if (status === OperationStatus.loading) {
                return;
            }
            try {
                this.startLoadingUserRoles();
                const userRoles = await appOf(this).gatewayClient.tprmUi().getTprmRoles(rpName);
                this.finishLoadingUserRoles(userRoles);
            } catch (error) {
                this.failLoadingUserRoles();
            }
        },
        async validateRbac(features: RbacFeature[], requiredAny = false) {
            // ensure we have user roles loaded which is dependency of user.features which is needed for this check
            const rpId = appOf(this).router.currentRoute.value.params.rpId;
            if (rpId) {
                if (!this.userRoles.status ||
                    this.userRoles.status === OperationStatus.uninitialized
                ) {
                    await this.loadUserRoles(rpId);
                } else if (this.userRoles.status === OperationStatus.loading) {
                    await waitForUserRoles(this);
                }
                const userFeatures = useUserStore().currentRpRbacFeatures || {} as Record<RbacFeature, boolean>;
                if (!hasSufficientFeatures(features, userFeatures, requiredAny)) {
                    await usePersistingErrorStore().showError({
                        reason: 'unauthorized-feature',
                    });
                }
            }
        },
    },
});
